/**
 * **El mapa completo de pantallas del AF §2**, y el registro que los próximos módulos
 * extienden.
 *
 * Están TODAS las pantallas de §2.1 (portal admin), §2.2 (portal profesores), §2.4
 * (tablet kiosko) y §2.5 (punto de acceso) — las tres superficies que cubre esta SPA. La
 * de §2.3 (app de socios) es otro cliente y no vive acá.
 *
 * Cada entrada declara:
 * - `status: "built"` → la pantalla existe de verdad y aparece en la sidebar.
 * - `status: "pending"` → la ruta EXISTE y renderiza `<ModuleNotBuilt>`, que dice qué
 *   módulo la construye. **No aparece en la sidebar** hasta estar construida, pero sí en
 *   este archivo: es el mapa que el próximo agente lee para saber qué le toca.
 *
 * Un stub silencioso sería peor que no tener la ruta: alguien creería que la pantalla
 * está y no anda. Por eso `ModuleNotBuilt` es explícito (CLAUDE.md, alcance §1.3).
 *
 * **Cómo agrega su pantalla un módulo**: ver `web/README.md`.
 */
import type { LucideIcon } from "lucide-react";
import { USER_ROLES, type RankedRole, type UserRole } from "@viva/shared";
import type { Area } from "../lib/auth/surface.js";
import type { SectionId } from "./sections.js";

export type ScreenStatus = "built" | "pending";

export interface ScreenDef {
  /** Identificador estable. Se usa en tests y en el registro de nav. */
  id: string;
  /** Nombre EXACTO de la pantalla en el AF §2. */
  title: string;
  /** Ruta completa, con su área adelante. */
  path: string;
  area: Area;
  section: SectionId;
  /** Sección del AF que la especifica. */
  spec: string;
  status: ScreenStatus;
  /** Rol mínimo jerárquico (RN-ROL-02). Excluyente con `roles`. */
  minRole?: RankedRole;
  /** Lista explícita de roles, cuando el permiso no es jerárquico. */
  roles?: UserRole[];
  /** Qué hace la pantalla, en una línea (sale del propósito del AF §2). */
  purpose?: string;
  /** Módulo que la construye. Lo muestra `ModuleNotBuilt`. */
  module?: string;
  /** Existe como ruta pero no como ítem de menú (detalles, hijas, wizards). */
  hidden?: boolean;
  icon?: LucideIcon;
}

const S = (screen: ScreenDef): ScreenDef => screen;

/** Pantallas que ve **cualquier** sesión de staff, contador sólo-lectura incluido. */
const EVERY_ROLE: UserRole[] = [...USER_ROLES];

/** Quienes cursan la capacitación desde el portal admin: todo el staff menos el profesor. */
const STAFF_LEARNERS: UserRole[] = USER_ROLES.filter((role) => role !== "teacher");

// ─────────────────────────────────────────────────────────────────────────────
// §2.1 Portal admin
// ─────────────────────────────────────────────────────────────────────────────

const ADMIN_SCREENS: ScreenDef[] = [
  // ── §12 Dashboard (RN-REP-01/02) ──────────────────────────────────────────
  S({
    id: "dashboard",
    title: "Dashboard del dueño",
    path: "/admin",
    area: "admin",
    section: "inicio",
    spec: "§12",
    status: "built",
    roles: ["owner", "admin"],
    purpose:
      "Los tres números del día (facturación, asistentes, ventas vs objetivo) con comparativas y el aviso de las 21:00 al celular.",
  }),

  // ── §3 Socios ─────────────────────────────────────────────────────────────
  S({
    id: "socios.alta",
    title: "Alta presencial de socio",
    path: "/admin/socios/alta",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    minRole: "receptionist",
    purpose:
      "Alta en mostrador con chequeo de duplicados en vivo, matrícula automática y derivación de la firma a la tablet.",
  }),
  S({
    id: "socios.padron",
    title: "Padrón de socios",
    path: "/admin/socios",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    minRole: "receptionist",
    purpose:
      "Búsqueda y filtros por estado, aptos por vencer, fichas incompletas, cesión de imagen, menores, convenios y duplicados.",
  }),
  S({
    id: "socios.ficha",
    title: "Ficha 360° del socio",
    path: "/admin/socios/:memberId",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    minRole: "receptionist",
    hidden: true,
    purpose:
      "Vista única del socio: datos, membresía, pagos, asistencias, reservas, documentos firmados, excepciones y auditoría.",
  }),
  S({
    id: "socios.grupos",
    title: "Grupos familiares",
    path: "/admin/socios/grupos-familiares",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    minRole: "receptionist",
    purpose:
      "Armar grupos con domicilio compartido, aplicar el descuento y asignar el pagador único.",
  }),
  S({
    id: "socios.convenios",
    title: "Convenios, canjes y cortesías",
    path: "/admin/socios/convenios",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "ABM de convenios con empresas y registro auditado de canjes y cortesías con autorizante y porcentaje.",
  }),
  S({
    id: "socios.visitantes",
    title: "Visitantes externos",
    path: "/admin/socios/visitantes",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    minRole: "receptionist",
    purpose:
      "Padrón de visitantes SportClub/Wellhub, vigencia y apto, ingresos por local y exportación para conciliar.",
  }),
  S({
    id: "socios.retiro-emergencia",
    title: "Ficha de retiro por emergencia",
    path: "/admin/socios/retiros-emergencia",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    /*
     * La matriz del modelo de datos §3.1 le da a Recepción `S (crear)` sobre
     * `emergencyWithdrawals`, y el backend exige `receptionist` para el POST: es
     * recepción la que llena la ficha en el mostrador cuando pasa la emergencia. El
     * `branch_manager` que tenía esta línea la dejaba fuera de la pantalla que el AF le
     * pide llenar. **Avisar al contacto de emergencia** sí es de encargado+ (RN-SOC-42),
     * y eso lo gatea la propia pantalla.
     */
    minRole: "receptionist",
    purpose:
      "Registro inmutable del episodio médico y del retiro, con firma en tablet y aviso al contacto de emergencia.",
  }),
  S({
    /*
     * NO está en el mapa de pantallas del AF §2.1, pero §3.7 (RN-SOC-24/26) exige que cada
     * texto legal sea un documento versionado y el backend ya lo expone
     * (`/api/legal-doc-versions`). Sin esta pantalla, publicar una versión nueva del
     * reglamento sólo se podría hacer con curl. Se agrega declarándolo, no en silencio.
     */
    id: "socios.textos-legales",
    title: "Textos legales",
    path: "/admin/socios/textos-legales",
    area: "admin",
    section: "socios",
    spec: "§3.7",
    status: "built",
    roles: ["owner", "admin"],
    purpose:
      "Publicar versiones nuevas del reglamento, la cesión de imagen y las autorizaciones, con re-aceptación opcional.",
  }),
  S({
    id: "socios.duplicados",
    title: "Fusión de duplicados",
    path: "/admin/socios/duplicados",
    area: "admin",
    section: "socios",
    spec: "§3",
    status: "built",
    minRole: "admin",
    purpose:
      "Detectar y fusionar fichas duplicadas conservando el número de socio más antiguo, con deshacer a 30 días.",
  }),

  // ── §4 Planes y cobranzas ─────────────────────────────────────────────────
  S({
    id: "planes.catalogo",
    title: "Planes y precios",
    path: "/admin/planes",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    // La única pantalla de planes (AF §4.10). Recepción y encargados ven la tab Planes en
    // sólo lectura (venden con esto); publicar, el wizard, el flyer, la lista de precios y el
    // historial son de dueños y administración (`canEditFinancialConfig`, RN-PLA-10), y la
    // API lo protege igual.
    minRole: "receptionist",
    purpose:
      "Los planes que se venden hoy en tarjetas por familia con sus precios y su vigencia, el wizard de alta y edición, el flyer para imprimir, la lista de precios versionada y el historial de aumentos.",
  }),
  S({
    id: "planes.promociones",
    title: "Promociones",
    path: "/admin/planes/promociones",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    roles: ["owner", "admin"],
    purpose: "ABM de promos con vigencia, planes alcanzados, cupo y reporte de uso por sede.",
  }),
  S({
    id: "planes.flyers",
    title: "Flyers de precios",
    path: "/admin/planes/flyers",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    roles: ["owner", "admin"],
    purpose:
      "Los flyers mensuales de precios generados desde la lista vigente, listos para exportar en PNG y PDF.",
  }),
  S({
    id: "planes.flyers.editor",
    title: "Editor de flyer",
    path: "/admin/planes/flyers/:flyerId",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    roles: ["owner", "admin"],
    hidden: true,
    purpose: "Textos, planes y lista del flyer con vista previa en vivo y exportación.",
  }),
  S({
    id: "planes.cuenta-corriente",
    title: "Cuenta corriente del socio",
    path: "/admin/planes/cuenta-corriente/:memberId",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    minRole: "receptionist",
    hidden: true,
    purpose: "Estado del plan, pagos, débito, congelamientos, acuerdos y excepciones del socio.",
  }),
  S({
    id: "planes.cobrar",
    title: "Cobrar en mostrador",
    path: "/admin/planes/cobrar",
    area: "admin",
    section: "planes",
    spec: "§4",
    // La pantalla es de §4 pero vive sobre los endpoints de Caja (§8): la construyó el
    // módulo de Caja junto con el resto del circuito del dinero (RN-CAJ-01).
    status: "built",
    module: "Caja y conciliación (§8)",
    minRole: "receptionist",
    purpose:
      "Cobro presencial (efectivo, Payway, transferencia) con matrícula, promos, grupo familiar y pases.",
  }),
  S({
    id: "planes.debitos",
    title: "Débitos automáticos",
    path: "/admin/planes/debitos",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    // El panel lo LEE recepción (docs/api/cobranzas.md); las acciones se gatean adentro:
    // reintento manual encargado+, adhesión/baja/corrida dueños y administración.
    minRole: "receptionist",
    purpose:
      "Panel del ciclo Payway: adhesiones, programados, rechazos con reintentos, bajas con motivo de churn.",
  }),
  S({
    id: "planes.debitos-archivo",
    title: "Débito por archivo",
    path: "/admin/planes/debitos/archivo",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    // VTCS-60: el débito clásico de Prisma por archivo (PrismaNet). Lo operan Dueño y
    // Administración; el alta de una tarjeta suelta la hace también recepción, desde
    // «Débitos automáticos» (AltaTarjetaArchivoModal).
    roles: ["owner", "admin"],
    purpose:
      "Débito por archivo de Prisma: migrar adheridos, presentar débitos, cargar rendiciones y pasar a token.",
  }),
  S({
    id: "planes.deudores",
    title: "Deudores",
    path: "/admin/planes/deudores",
    area: "admin",
    section: "planes",
    spec: "§4",
    status: "built",
    // `GET /api/debtors` es de recepción: reclamar la deuda es trabajo del mostrador.
    // El export es de encargado+, el acuerdo de administración y condonar, del dueño.
    minRole: "receptionist",
    purpose: "Mora por sede/antigüedad con avisos enviados, acuerdos de pago y condonaciones.",
  }),

  // ── §5 Clases ─────────────────────────────────────────────────────────────
  S({
    id: "clases.grilla",
    title: "Editor de grilla mensual",
    path: "/admin/clases/grilla",
    area: "admin",
    section: "clases",
    spec: "§5",
    status: "built",
    // RN-CLA-05 / PV-104: la arman y publican el encargado de sede, el coordinador
    // intersedes y Administración (todas las sedes).
    minRole: "branch_manager",
    purpose:
      "Armar, clonar y publicar la grilla del mes por sede, con validación de choques, feriados y exportación.",
  }),
  S({
    id: "clases.grilla-consulta",
    title: "Grilla de la sede",
    // Recepción → Ficha de clase: la grilla de SU sede activa en sólo lectura (sin editar
    // ni publicar). Su tarjeta «Clases de la sede» vive en el grupo Clases (`groups.ts`).
    path: "/admin/clases/grilla/consulta",
    area: "admin",
    section: "clases",
    spec: "§5",
    status: "built",
    roles: ["receptionist"],
    purpose:
      "Consultar la grilla del mes de la sede y abrir la ficha de una clase para reservar por un socio o anotar una prueba.",
  }),
  S({
    id: "clases.ficha",
    title: "Ficha de clase",
    // El parámetro es la CLASE de la grilla (`scheduledClasses`), no una fecha: la ficha
    // del AF muestra fijos, solicitudes y ocupación histórica de la clase, y elige la
    // fecha adentro (`?fecha=<sessionId>`). Se renombró de `:sessionId` a `:classId` al
    // construirla, para que la ruta no mienta sobre lo que recibe.
    path: "/admin/clases/:classId",
    area: "admin",
    section: "clases",
    spec: "§5",
    status: "built",
    minRole: "receptionist",
    hidden: true,
    purpose:
      "Fijos y solicitudes, inscriptos y lista de espera, cancelaciones auditadas y correcciones de asistencia.",
  }),
  S({
    id: "clases.ocupacion",
    title: "Reporte de ocupación y asistencia",
    path: "/admin/clases/ocupacion",
    area: "admin",
    section: "clases",
    spec: "§5",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "Ocupación y asistencia real por clase/disciplina/sede para decidir las bajas mensuales de grilla.",
  }),
  S({
    id: "clases.eventos",
    title: "Eventos (master / Viva Games)",
    path: "/admin/clases/eventos",
    area: "admin",
    section: "clases",
    spec: "§5",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "Crear y publicar clases master y Viva Games con cupo y precio, cobrar en mostrador y cancelar con devolución.",
  }),

  // ── §6 Acceso ─────────────────────────────────────────────────────────────
  S({
    id: "acceso.puntos",
    title: "Gestión de puntos de acceso",
    path: "/admin/acceso/puntos",
    area: "admin",
    section: "acceso",
    spec: "§6",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose:
      "Alta/estado de molinetes y QR por local, regeneración del QR de local y parámetros de acceso.",
  }),
  S({
    id: "acceso.ingresos",
    title: "Registro de ingresos",
    path: "/admin/acceso/ingresos",
    area: "admin",
    section: "acceso",
    spec: "§6",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "Registro completo y filtrable de cada intento de ingreso (medio, resultado, motivo, excepciones, contingencia), con exportación.",
  }),

  // ── §7 Personal y liquidaciones ───────────────────────────────────────────
  S({
    id: "profesores.legajos",
    title: "Personal y legajos",
    path: "/admin/personal",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose: "Alta y gestión de legajos: tipo, contratación, sede, categoría de tarifa y estado.",
  }),
  S({
    id: "profesores.tarifario",
    title: "Tarifario y multiplicadores",
    path: "/admin/personal/tarifario",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose:
      "Versionar tarifas por categoría, tarifas individuales, multiplicadores por tipo de día y feriados.",
  }),
  S({
    id: "profesores.turnos",
    title: "Turnos y fichajes",
    path: "/admin/personal/turnos",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "Grilla de turnos de sala y recepción y resolución de discrepancias de fichadas antes de liquidar.",
  }),
  S({
    id: "profesores.objetivos",
    title: "Objetivos y comisiones de recepción",
    path: "/admin/personal/objetivos",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose: "Objetivo mensual por recepcionista, escala de comisión y seguimiento de % alcanzado.",
  }),
  S({
    id: "profesores.mis-comisiones",
    title: "Mis comisiones",
    path: "/admin/personal/mis-comisiones",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["receptionist"],
    purpose: "Objetivo propio, ventas atribuidas, tramo actual y comisión proyectada en vivo.",
  }),
  S({
    id: "profesores.varas",
    title: "Varas de premio",
    path: "/admin/personal/varas",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose:
      "Vara vigente, promedio del mes, vara proyectada e histórico por clase de la grilla y ajuste con motivo.",
  }),
  S({
    id: "profesores.preliquidacion",
    title: "Pre-liquidación mensual",
    path: "/admin/personal/liquidaciones",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "Revisar el cálculo del mes por persona (horas, premios, pruebas, comisiones), ajustar y cerrar.",
  }),
  S({
    id: "profesores.detalle-liquidacion",
    title: "Detalle de liquidación",
    path: "/admin/personal/liquidaciones/:settlementId",
    area: "admin",
    section: "profesores",
    spec: "§7",
    status: "built",
    minRole: "branch_manager",
    hidden: true,
    purpose:
      "Detalle clase por clase: presentes, vara, premio, tarifa y multiplicador; genera ajustes retroactivos.",
  }),

  // ── §8 Caja ───────────────────────────────────────────────────────────────
  S({
    id: "caja.turno",
    title: "Caja del turno",
    path: "/admin/caja",
    area: "admin",
    section: "caja",
    spec: "§8",
    status: "built",
    module: "Caja y conciliación (§8)",
    minRole: "receptionist",
    purpose:
      "Abrir turno, registrar cobros, descuentos, gastos menores y preparar sobres; cierre a ciegas.",
  }),
  S({
    id: "caja.cierre",
    title: "Cierre de turno",
    path: "/admin/caja/cierre",
    area: "admin",
    section: "caja",
    spec: "§8",
    status: "built",
    module: "Caja y conciliación (§8)",
    minRole: "receptionist",
    purpose:
      "Declaración a ciegas del efectivo, comparativo esperado vs declarado, sobre y retiro con doble confirmación.",
  }),
  S({
    id: "caja.sobres-profes",
    title: "Sobres de profes",
    path: "/admin/caja/sobres-profes",
    area: "admin",
    section: "caja",
    spec: "§7",
    status: "built",
    module: "Profesores, liquidación y comisiones (§7)",
    minRole: "receptionist",
    purpose:
      "Entregar el sobre de pago de cada profe con el código que le llega por email; el listado del mes con quién entregó y cuándo.",
  }),
  S({
    id: "caja.historial",
    title: "Historial de cierres y sobres",
    path: "/admin/caja/historial",
    area: "admin",
    section: "caja",
    spec: "§8",
    status: "built",
    module: "Caja y conciliación (§8)",
    roles: ["owner", "admin"],
    purpose: "Todos los cierres, diferencias, remanentes y la traza completa de sobres retirados.",
  }),
  S({
    id: "caja.conciliacion",
    title: "Conciliación",
    path: "/admin/caja/conciliacion",
    area: "admin",
    section: "caja",
    spec: "§8",
    status: "built",
    module: "Caja y conciliación (§8)",
    roles: ["owner", "admin"],
    purpose:
      "Transferencias recibidas en Mercado Pago, Payway (débitos y lotes) y liquidaciones de convenios.",
  }),
  S({
    id: "caja.diferencias",
    title: "Diferencias y arqueos",
    path: "/admin/caja/diferencias",
    area: "admin",
    section: "caja",
    spec: "§8",
    status: "built",
    module: "Caja y conciliación (§8)",
    roles: ["owner", "admin", "intersede_coordinator"],
    purpose:
      "Diferencias con responsable y estado, ranking acumulado por usuario/sede e inicio de arqueos sorpresa.",
  }),
  S({
    id: "caja.configuracion",
    // NO está en el mapa de pantallas del AF §2: el AF describe las cuentas de la SRL
    // (RN-CAJ-19.1) y las 3 terminales (RN-CAJ-16) como datos, sin pantalla propia. El
    // backend las expone (`/api/bank-accounts`, `/api/pos-terminals`) y sin un lugar donde
    // cargarlas no hay transferencia conciliable ni cobro por posnet: se agrega acá, dicho.
    title: "Configuración de caja",
    path: "/admin/caja/configuracion",
    area: "admin",
    section: "caja",
    spec: "§8 (fuera del mapa de §2)",
    status: "built",
    module: "Caja y conciliación (§8)",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose:
      "Cuentas bancarias de la SRL (alias/CBU, visible al socio, Interbanking) y terminales Payway por sede.",
  }),

  // ── §9 Contable y kiosko ──────────────────────────────────────────────────
  S({
    id: "contable.comprobantes",
    title: "Comprobantes",
    path: "/admin/contable/comprobantes",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Contable y ARCA (§9)",
    // El contador entra por lista explícita: está fuera de la jerarquía (RN-ROL-01).
    roles: ["owner", "admin", "accountant_readonly"],
    purpose:
      "Facturas y notas de crédito: facturar a demanda, emitir notas de crédito, reintentar el CAE y reenviar por mail.",
  }),
  // Multi-emisor ARCA (oct-2026): CUITs, certificado (CSR → .crt), PV por sede. Roles
  // explícitos como Integraciones: leen Dueño y Administración, escribe el Dueño.
  S({
    id: "contable.emisores",
    title: "Emisores fiscales",
    path: "/admin/contable/emisores",
    area: "admin",
    section: "contable",
    spec: "§9 (ext.)",
    status: "built",
    module: "Contable y ARCA (§9)",
    roles: ["owner", "admin"],
    purpose:
      "Los CUITs que facturan en ARCA: pedido de certificado, carga del certificado, prueba de conexión y punto de venta por sede.",
  }),
  S({
    id: "contable.exportes",
    title: "Exportes contables",
    path: "/admin/contable/exportes",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Contable y ARCA (§9)",
    roles: ["owner", "admin", "accountant_readonly"],
    purpose:
      "Libro IVA Ventas, gastos, cobros por medio y ventas de kiosko; envío mensual al contador.",
  }),
  S({
    id: "contable.egresos",
    title: "Egresos",
    path: "/admin/contable/egresos",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Contable y ARCA (§9)",
    /**
     * RN-CON-12: **recepción y el encargado cargan los egresos de su sede**, así que el
     * mínimo no es encargado. La lista calca a `GET /api/expenses`, que admite al contador
     * y **no** al coordinador intersedes (ver `docs/api/needs-web.md` §9).
     */
    roles: ["owner", "admin", "branch_manager", "receptionist", "accountant_readonly"],
    purpose:
      "Gastos por sede con categoría y comprobante adjunto, aprobación/rechazo y caja chica.",
  }),
  S({
    id: "contable.proveedores",
    title: "Proveedores",
    path: "/admin/contable/proveedores",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Contable y ARCA (§9)",
    // El contador lee el padrón (lo pide el libro de gastos), pero no lo edita.
    roles: ["owner", "admin", "accountant_readonly"],
    purpose: "Padrón de proveedores con modalidad, historial de compras y registro de compra.",
  }),
  S({
    id: "contable.resultado",
    title: "Resultado por sede",
    path: "/admin/contable/resultado",
    area: "admin",
    section: "contable",
    spec: "§9.2",
    // La construye Reportes (§12): comparte servicios, exportes y reglas de atribución con
    // la rentabilidad por actividad (RN-CON-18bis / RN-REP-14).
    status: "built",
    module: "Reportes y alertas (§12)",
    roles: ["owner", "admin"],
    purpose:
      "Ingresos menos egresos por mes y sede (con prorrateo de Central) y el consolidado, más la solapa «Por actividad».",
  }),
  S({
    id: "contable.convenios",
    title: "Liquidación de convenios",
    path: "/admin/contable/convenios",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Contable y ARCA (§9)",
    // Calca a la API: el coordinador y el contador LEEN; las acciones son de `admin`+.
    roles: ["owner", "admin", "intersede_coordinator", "accountant_readonly"],
    purpose: "Pre-liquidación mensual por ingreso de SportClub/Wellhub, conciliación y Factura A.",
  }),
  S({
    id: "contable.productos",
    title: "Catálogo de productos",
    path: "/admin/kiosko/productos",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Kiosko y stock (§9)",
    roles: ["owner", "admin"],
    purpose: "Productos con precio único para las 3 sedes, costo, vencimiento y stock mínimo.",
  }),
  S({
    // Pedido del 23-oct-2026: los proveedores del kiosko, aparte de los del gimnasio.
    id: "kiosko.proveedores",
    title: "Proveedores del kiosko",
    path: "/admin/kiosko/proveedores",
    area: "admin",
    section: "contable",
    spec: "§9 (ext.)",
    status: "built",
    module: "Kiosko y stock (§9)",
    // Los mismos que editan el catálogo de productos.
    roles: ["owner", "admin"],
    purpose:
      "A quién se le compra cada producto del kiosko, con su código y su último precio. No son los proveedores del gimnasio.",
  }),
  S({
    // Pedido del 23-oct-2026: compras a los proveedores del kiosko (suman stock, último costo).
    id: "kiosko.compras",
    title: "Compras del kiosko",
    path: "/admin/kiosko/compras",
    area: "admin",
    section: "contable",
    spec: "§9 (ext.)",
    status: "built",
    module: "Kiosko y stock (§9)",
    // Los mismos que ven los proveedores del kiosko y editan el catálogo.
    roles: ["owner", "admin"],
    purpose:
      "Compras a los proveedores del kiosko: suman stock a la sede, fijan el último costo y, en efectivo, salen de la caja del kiosko.",
  }),
  S({
    id: "contable.stock",
    title: "Stock y conteo",
    path: "/admin/kiosko/stock",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Kiosko y stock (§9)",
    // RN-CON-29: **el conteo físico lo cargan las recepcionistas** (rel. 11.4).
    minRole: "receptionist",
    purpose:
      "Stock teórico y movimientos por sede, conteo físico, mermas y ajustes, lotes por vencer.",
  }),
  S({
    // Pedido del 22-oct-2026: recepción registra el consumo interno sin pasar por el conteo.
    id: "kiosko.consumo-interno",
    title: "Consumo interno",
    path: "/admin/kiosko/consumo-interno",
    area: "admin",
    section: "contable",
    spec: "§9 (ext.)",
    status: "built",
    module: "Kiosko y stock (§9)",
    // El mismo permiso que `POST /api/stock/internal-consumption`.
    minRole: "receptionist",
    purpose:
      "Lo que alguien del equipo se lleva del kiosko a su nombre: baja el stock de la sede, no es venta ni egreso.",
  }),
  S({
    id: "contable.pos",
    title: "Punto de venta de kiosko (réplica)",
    path: "/admin/kiosko/venta",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Kiosko y stock (§9)",
    minRole: "receptionist",
    purpose: "La misma pantalla de venta de la tablet, replicada en el portal de recepción.",
  }),
  S({
    id: "kiosko.ventas",
    title: "Ventas del kiosko",
    path: "/admin/kiosko/ventas",
    area: "admin",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Kiosko y stock (§9)",
    minRole: "receptionist",
    purpose:
      "Historial de ventas del mostrador y de la tablet pública, con medio, canal y comprobante.",
  }),
  S({
    // Extensión del 22-sep-2026: la tienda del kiosko que cobra con QR de Mercado Pago.
    id: "kiosko.tablets",
    title: "Tablets públicas",
    path: "/admin/kiosko/tablets",
    area: "admin",
    section: "contable",
    spec: "§9 (ext.)",
    status: "built",
    module: "Kiosko y stock (§9)",
    minRole: "branch_manager",
    purpose:
      "Links de la tienda del kiosko para dejar abierta en una tablet: el socio compra y paga con QR.",
  }),

  // ── §10 CRM ───────────────────────────────────────────────────────────────
  S({
    id: "crm.interesados",
    title: "Interesados",
    path: "/admin/crm/interesados",
    area: "admin",
    section: "crm",
    spec: "§10",
    status: "built",
    minRole: "receptionist",
    purpose: "Interesados por etapa con filtros, tareas vencidas y conversión a socio.",
  }),
  S({
    id: "crm.ficha-interesado",
    title: "Ficha de interesado",
    path: "/admin/crm/interesados/:prospectId",
    area: "admin",
    section: "crm",
    spec: "§10",
    status: "built",
    minRole: "receptionist",
    hidden: true,
    purpose: "Datos, origen, línea de tiempo de contactos, clase de prueba y conversión.",
  }),
  S({
    id: "crm.campanas",
    title: "Campañas y segmentos",
    path: "/admin/crm/campanas",
    area: "admin",
    section: "crm",
    spec: "§10",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator"],
    purpose: "Armar y programar campañas de retención por segmento con medición de resultado.",
  }),
  S({
    id: "crm.moderacion",
    title: "Moderación y noticias",
    path: "/admin/crm/moderacion",
    area: "admin",
    section: "crm",
    spec: "§10",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator"],
    purpose: "Cola de moderación del muro social y publicación de noticias.",
  }),
  S({
    id: "crm.mensajes",
    title: "Bandeja de mensajes de sede",
    path: "/admin/crm/mensajes",
    area: "admin",
    section: "crm",
    spec: "§10",
    status: "built",
    minRole: "receptionist",
    purpose: "Conversaciones socio↔sede registradas, ordenadas por sin responder.",
  }),

  // ── §11 Usuarios, roles y auditoría ───────────────────────────────────────
  S({
    id: "roles.usuarios",
    title: "Usuarios y permisos",
    path: "/admin/usuarios",
    area: "admin",
    section: "roles",
    spec: "§11",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose: "Alta, rol, sedes asignadas, rotación, desactivación inmediata y sesiones activas.",
  }),
  S({
    id: "roles.sede-activa",
    title: "Selector de sede activa",
    path: "/admin/sede-activa",
    area: "admin",
    section: "roles",
    spec: "§11",
    status: "built",
    // Todo el que opera necesita saber con qué sede opera (D6), el contador incluido.
    roles: EVERY_ROLE,
    purpose: "Elegir la sede que imputa caja, stock y operaciones para usuarios multi-sede.",
  }),
  S({
    id: "roles.auditoria",
    title: "Registro de auditoría",
    path: "/admin/auditoria",
    area: "admin",
    section: "roles",
    spec: "§11",
    status: "built",
    roles: ["owner", "admin"],
    purpose:
      "Consulta inmutable de toda acción (quién/qué/cuándo/desde dónde, antes/después, excepciones).",
  }),
  S({
    id: "roles.alertas-seguridad",
    title: "Alertas de seguridad",
    path: "/admin/alertas-seguridad",
    area: "admin",
    section: "roles",
    spec: "§11",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator"],
    purpose:
      "Catálogo configurable de alertas (anulaciones, descuentos, diferencias) con umbrales y destinatarios.",
  }),

  // ── §12 Reportes ──────────────────────────────────────────────────────────
  //
  // Las cinco pantallas del mapa de §2.1 son «Dashboard del dueño», «Reporte de
  // facturación», «Reporte de asistencia», «Objetivos por recepcionista» y «Alertas de
  // negocio». Los otros cuatro reportes que §12.3 especifica —recaudación mensual
  // (RN-REP-07), socios activos (RN-REP-04), no renovados / en riesgo (RN-REP-08) y los
  // cierres de caja a Administración (RN-REP-09)— NO tienen fila propia en §2.1, pero son
  // reportes con su endpoint, su recorte por rol y su exporte auditado. Se registran acá
  // en vez de esconderlos dentro de otra pantalla: sin ruta no se pueden abrir, y sin
  // pantalla el alcance quedaría recortado en silencio (AF §1.3).
  S({
    id: "reportes.facturacion",
    title: "Reporte de facturación",
    path: "/admin/reportes/facturacion",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    // RN-CON-18: recepción y encargado ven el DÍA de su sede. La API los recorta (y
    // rechaza un rango largo con 403); esconderles la pantalla les taparía su propio día.
    // RN-ROL-01 (PV-82): el Contador lee los reportes de facturación, en sólo lectura.
    roles: [
      "owner",
      "admin",
      "intersede_coordinator",
      "branch_manager",
      "receptionist",
      "accountant_readonly",
    ],
    purpose:
      "Facturación por día/mes, sede, medio, concepto y actividad, hasta el pago puntual y su auditoría.",
  }),
  S({
    id: "reportes.recaudacion",
    title: "Recaudación mensual",
    path: "/admin/reportes/recaudacion",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    // AF §11, caso borde: el Coordinador intersedes NO ve la recaudación mensual.
    roles: ["owner", "admin", "accountant_readonly"],
    purpose:
      "Acumulado del mes por sede y consolidado, con comparativa contra el mes anterior y el año anterior.",
  }),
  S({
    id: "reportes.asistencia",
    title: "Reporte de asistencia",
    path: "/admin/reportes/asistencia",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    minRole: "receptionist",
    purpose: "Asistentes únicos por sede/día/franja/actividad, visitantes de convenios separados.",
  }),
  S({
    id: "reportes.socios-activos",
    title: "Socios activos",
    path: "/admin/reportes/socios-activos",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    minRole: "receptionist",
    purpose:
      "Total y por sede de asociación con la definición operativa a la vista, y la serie histórica mensual.",
  }),
  S({
    id: "reportes.objetivos",
    title: "Objetivos por recepcionista",
    path: "/admin/reportes/objetivos",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    // RN-REP-06: cada recepcionista ve el suyo. El alcance lo recorta la API (`scope`).
    minRole: "receptionist",
    purpose: "Objetivo mensual de ventas y % de avance en vivo, visible según rol.",
  }),
  S({
    id: "reportes.en-riesgo",
    title: "No renovados / en riesgo",
    path: "/admin/reportes/en-riesgo",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    minRole: "receptionist",
    purpose:
      "Socios con 15+ días sin venir o cuota vencida, con el estado del contacto de retención.",
  }),
  S({
    id: "reportes.cierres",
    title: "Cierres de caja a Administración",
    path: "/admin/reportes/cierres",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    // El Contador de sólo lectura está fuera de la jerarquía: se lista explícito (PV-82).
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager", "accountant_readonly"],
    purpose:
      "Arqueos de cada turno: declarado vs. sistema, diferencia, quién cerró y cajas sin operaciones.",
  }),
  S({
    id: "reportes.alertas-negocio",
    title: "Alertas de negocio",
    path: "/admin/reportes/alertas",
    area: "admin",
    section: "reportes",
    spec: "§12",
    status: "built",
    module: "Reportes y alertas (§12)",
    // La lee también Administración; editarla es de Dueño y Coordinador (AF §12.4).
    roles: ["owner", "admin", "intersede_coordinator"],
    purpose: "Umbrales de caída de asistencia y facturación e historial de disparos.",
  }),

  // ── §13 Migración y puesta en marcha ──────────────────────────────────────
  S({
    id: "migracion.estado",
    title: "Estado de migración",
    path: "/admin/migracion",
    area: "admin",
    section: "migracion",
    spec: "§13",
    status: "built",
    roles: ["owner", "admin"],
    purpose: "Corridas de prueba y final con validaciones, diferencias y errores por fila.",
  }),
  S({
    id: "migracion.checklist",
    title: "Checklist de puesta en marcha",
    path: "/admin/migracion/checklist",
    area: "admin",
    section: "migracion",
    spec: "§13",
    status: "built",
    roles: ["owner", "admin"],
    purpose:
      "Carga inicial, validaciones y aprobaciones de módulos que habilitan la fecha de corte.",
  }),
  //
  // «Día de migración» no sale de la tabla de AF §2.1: es el asistente del viernes del
  // corte que ordena los pasos que ya existen (Crossfy, backup y lectura del terminal,
  // revisión, sincronización, apertura y reporte) con su estado real
  // (`GET /api/migration/day`). No duplica lógica: cada paso usa su endpoint de siempre.
  S({
    id: "migracion.dia",
    title: "Día de migración",
    path: "/admin/migracion/dia",
    area: "admin",
    section: "migracion",
    spec: "§13 (ext.)",
    status: "built",
    roles: ["owner", "admin"],
    purpose:
      "Los siete pasos del día del corte en orden, con su estado: Excel de Crossfy, backup y lectura del molinete, revisión, sincronización, apertura y reporte final.",
  }),
  //
  // «Corte y encendido por sede» NO sale de la tabla de AF §2.1 —que para §13 lista dos
  // pantallas— y se agrega igual, como «Textos legales» en §3: RN-MIG-11 separa el corte
  // de datos (único, 3 sedes) del encendido operativo (escalonado, sede por sede), y sin
  // pantalla propia congelar los sistemas viejos y prender una sede sólo se podrían hacer
  // con `curl`. Tiene además otro público: el **Encargado** lee el estado de su sede
  // («mi molinete todavía no opera» es el caso borde de §13); congelar es sólo del Dueño.
  S({
    id: "migracion.corte",
    title: "Corte y encendido por sede",
    path: "/admin/migracion/corte",
    area: "admin",
    section: "migracion",
    spec: "§13 (RN-MIG-08/11)",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "Congelar los sistemas viejos y prender acceso, reservas y kiosko sede por sede. La caja no se escalona.",
  }),

  // ── Configuración (superficie de `docs/api/estructura.md` y `devices.md`) ──
  //
  // Estas pantallas no salen de la tabla de AF §2.1: son la cara del módulo de estructura
  // del backend (sedes, locales, salones, disciplinas, parámetros, catálogos,
  // dispositivos, jobs, salud), que el AF describe repartido en §1.4, §5, §11 y la
  // arquitectura §9.2. Se declaran acá para que el mapa sea completo y honesto.
  S({
    id: "config.sedes",
    title: "Sedes y locales",
    path: "/admin/config/sedes",
    area: "admin",
    section: "config",
    spec: "§1.4 (D6)",
    status: "built",
    minRole: "branch_manager",
    purpose: "Las 3 sedes (más Central) y sus 6 locales; qué local tiene recepción.",
  }),
  S({
    id: "config.salones",
    title: "Salones",
    path: "/admin/config/salones",
    area: "admin",
    section: "config",
    spec: "§5",
    status: "built",
    minRole: "branch_manager",
    purpose: "Capacidad por disciplina y reimpresión del QR del salón (es inmutable).",
  }),
  S({
    id: "config.disciplinas",
    title: "Disciplinas",
    path: "/admin/config/disciplinas",
    area: "admin",
    section: "config",
    spec: "§5",
    status: "built",
    minRole: "branch_manager",
    purpose: "Límite semanal, restringida (derecho a recupero) y fijaciones permitidas.",
  }),
  S({
    id: "config.parametros",
    title: "Parámetros",
    path: "/admin/config/parametros",
    area: "admin",
    section: "config",
    spec: "§11",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose:
      "Parámetros del sistema con su valor por defecto, alcance (global o por sede) e historial de cambios.",
  }),
  S({
    id: "config.catalogos",
    title: "Catálogos",
    path: "/admin/config/catalogos",
    area: "admin",
    section: "config",
    spec: "§9 / §10",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose: "Categorías de gasto, orígenes de contacto, motivos de baja y demás listas editables.",
  }),
  S({
    id: "config.dispositivos",
    title: "Dispositivos",
    path: "/admin/config/dispositivos",
    area: "admin",
    section: "config",
    spec: "Contrato §3",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "Tablets del kiosko y lectores de acceso: vinculación con una clave que se muestra una sola vez, señal de vida y revocación.",
  }),
  //
  // «Molinetes ZKTeco» no sale del mapa de §2.1: es la cara del terminal de huella del
  // molinete (`backend/src/modules/acceso/zk.routes.ts`, `docs/decisiones/molinete-zkteco.md`).
  // Recepción la ve y abre desde acá (RN-ACC-07); encargado+ sincroniza, lee y revisa;
  // Dueño y Administración dan de alta, editan y dan de baja (gating fino adentro).
  S({
    id: "config.zkteco",
    title: "Molinetes ZKTeco",
    path: "/admin/config/dispositivos/zkteco",
    area: "admin",
    section: "config",
    spec: "§6 (RN-ACC-02/05/07)",
    status: "built",
    minRole: "branch_manager",
    purpose:
      "El terminal de huella de cada molinete: si está en línea, backup, cola, sincronización, revisión de usuarios sin socio y abrir el molinete.",
  }),
  //
  // «Integraciones» **no sale del mapa de §2.1**: nace del pedido de Damián del 3-sep-2026
  // («las credenciales de Payway, ARCA, Interbanking, Mercado Pago, molinetes, etc. no
  // pueden estar en un `.env`: tienen que ser configurables desde el perfil del admin»),
  // que el backend ya implementó en `/api/integrations`. Sin pantalla, cargar el
  // certificado fiscal o rotar la api key de Payway sólo se podría hacer con curl —
  // exactamente lo que el pedido venía a evitar. Se declara acá, como hizo §3 con «Textos
  // legales» y §12 con sus cuatro reportes, para que el mapa siga siendo completo y honesto.
  //
  // Roles explícitos, NO `minRole`: leen Dueño y Administración, y escribe **sólo el
  // Dueño** — el gating fino vive adentro de la pantalla porque no es jerárquico
  // (`docs/api/integraciones.md`, extensión de la matriz RN-ROL-02).
  S({
    id: "config.integraciones",
    title: "Integraciones",
    path: "/admin/config/integraciones",
    area: "admin",
    section: "config",
    spec: "§11 (ext.)",
    status: "built",
    roles: ["owner", "admin"],
    purpose:
      "Credenciales de Payway, ARCA, Interbanking, Mercado Pago, molinetes, email y avisos al celular: se cargan acá y quedan guardadas cifradas.",
  }),
  S({
    id: "config.jobs",
    // La pantalla (`JobsPage`) ya se titula así; «job» es jerga que el cliente no tiene que leer.
    title: "Tareas automáticas",
    path: "/admin/config/jobs",
    area: "admin",
    section: "config",
    spec: "Operación",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator"],
    purpose:
      "Las tareas automáticas del sistema (cierres, débitos, avisos): cuándo corren y el interruptor para apagar cada una.",
  }),
  S({
    id: "config.salud",
    title: "Salud del sistema",
    path: "/admin/config/salud",
    area: "admin",
    section: "config",
    spec: "Arq. §9.2",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator"],
    purpose:
      "Chequeo del sistema: base de datos, tareas automáticas, ARCA, Payway, feed bancario y dispositivos.",
  }),
  // ── Capacitación del staff (`docs/spec/capacitacion.md`) ────────────────
  // No salen del AF §2.1: son el curso de uso del sistema de AF §13.5 (RN-MIG-13/14).
  // El curso lo hace todo el staff; el profesor lo cursa desde su portal (abajo), así que
  // acá no figura: su área admin se limita a la sede y a su cuenta.
  S({
    id: "capacitacion.curso",
    title: "Curso de uso del sistema",
    path: "/admin/capacitacion",
    area: "admin",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-01)",
    status: "built",
    roles: STAFF_LEARNERS,
    purpose:
      "El curso de tu rol: videos, prácticas y evaluaciones lección por lección, y el examen final.",
  }),
  S({
    id: "capacitacion.leccion",
    title: "Lección del curso",
    path: "/admin/capacitacion/:lessonKey",
    area: "admin",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-04/05/06)",
    status: "built",
    roles: STAFF_LEARNERS,
    hidden: true,
    purpose: "Video, resumen, práctica y evaluación de una lección.",
  }),
  S({
    id: "capacitacion.final",
    title: "Examen final del curso",
    path: "/admin/capacitacion/final",
    area: "admin",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-08)",
    status: "built",
    roles: STAFF_LEARNERS,
    hidden: true,
    purpose: "El examen final, habilitado al completar todas las lecciones del rol.",
  }),
  S({
    id: "capacitacion.certificado",
    title: "Certificado del curso",
    path: "/admin/capacitacion/certificado",
    area: "admin",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-08)",
    status: "built",
    roles: STAFF_LEARNERS,
    hidden: true,
    purpose: "El certificado imprimible de quien aprobó el examen final.",
  }),
  S({
    id: "capacitacion.equipo",
    title: "Seguimiento de la capacitación",
    path: "/admin/capacitacion/equipo",
    area: "admin",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-12/13)",
    status: "built",
    roles: ["owner", "admin", "intersede_coordinator", "branch_manager"],
    purpose:
      "Quién hizo el curso, cuánto avanzó, qué nota sacó y qué preguntas se le complicaron al equipo.",
  }),

  // ── Mi cuenta ─────────────────────────────────────────────────────────────
  S({
    id: "cuenta.mi-cuenta",
    title: "Mi cuenta",
    path: "/admin/mi-cuenta",
    area: "admin",
    section: "cuenta",
    spec: "§11",
    status: "built",
    roles: EVERY_ROLE,
    purpose: "Datos propios, sedes, cambio de contraseña, QR y PIN de fichaje y sesiones abiertas.",
  }),
];

// ─────────────────────────────────────────────────────────────────────────────
// §2.2 Portal profesores (sin ninguna función de dinero — rel. 6.14)
// ─────────────────────────────────────────────────────────────────────────────

const TEACHER_SCREENS: ScreenDef[] = [
  S({
    id: "profes.home",
    title: "Inicio del profe",
    path: "/profes",
    area: "profes",
    section: "inicio",
    spec: "§2.2",
    status: "built",
    roles: ["teacher"],
    purpose: "Punto de entrada del portal de profesores: acceso rápido a todas sus pantallas.",
  }),
  S({
    id: "profes.agenda",
    title: "Agenda del profe",
    path: "/profes/agenda",
    area: "profes",
    section: "clases",
    spec: "§5",
    status: "built",
    roles: ["teacher"],
    purpose:
      "Sus clases de la semana, inscriptos vs cupo, avisos y habilitación para personalizados.",
  }),
  S({
    id: "profes.lista-clase",
    title: "Lista de clase",
    path: "/profes/clases/:sessionId",
    area: "profes",
    section: "clases",
    spec: "§5",
    status: "built",
    roles: ["teacher"],
    hidden: true,
    purpose:
      "Reservados/presentes/lista de espera en vivo y corrección de asistencia hasta el minuto 15.",
  }),
  S({
    id: "profes.fichadas",
    title: "Mis fichadas",
    path: "/profes/fichadas",
    area: "profes",
    section: "acceso",
    spec: "§6",
    status: "built",
    roles: ["teacher"],
    purpose:
      "Fichadas del mes con correcciones marcadas, fichaje desde el celular y reporte de olvidos.",
  }),
  S({
    id: "profes.mi-qr",
    title: "Mi QR de fichaje",
    path: "/profes/mi-qr",
    area: "profes",
    section: "acceso",
    spec: "§6 / §7",
    status: "built",
    roles: ["teacher"],
    purpose: "Tu QR personal para fichar entrada y salida en la tablet de la sede.",
  }),
  S({
    id: "profes.mi-mes",
    title: "Mi mes",
    path: "/profes/mi-mes",
    area: "profes",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["teacher"],
    purpose: "Liquidación en curso: horas, premios, vara vigente y total proyectado.",
  }),
  S({
    id: "profes.mis-clases",
    title: "Mis clases",
    path: "/profes/mis-clases",
    area: "profes",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["teacher"],
    purpose:
      "Agenda con presentes por clase, completa, reemplazos y pruebas agendadas sin datos de contacto.",
  }),
  S({
    id: "profes.liquidaciones",
    title: "Mis liquidaciones",
    path: "/profes/liquidaciones",
    area: "profes",
    section: "profesores",
    spec: "§7",
    status: "built",
    roles: ["teacher"],
    purpose: "Historial de liquidaciones cerradas; para externos, monto a facturar y estado.",
  }),
  S({
    id: "profes.avisos",
    title: "Avisos del profesor",
    path: "/profes/avisos",
    area: "profes",
    section: "crm",
    spec: "§10",
    status: "built",
    roles: ["teacher"],
    purpose: "Clases de prueba agendadas y cancelaciones de su grilla.",
  }),
  // El curso de uso del sistema del profesor (`docs/spec/capacitacion.md`).
  S({
    id: "profes.capacitacion",
    title: "Capacitación",
    path: "/profes/capacitacion",
    area: "profes",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-01)",
    status: "built",
    roles: ["teacher"],
    purpose: "Tu curso de uso del portal: videos, evaluaciones y el examen final.",
  }),
  S({
    id: "profes.capacitacion.leccion",
    title: "Lección del curso",
    path: "/profes/capacitacion/:lessonKey",
    area: "profes",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-04/05/06)",
    status: "built",
    roles: ["teacher"],
    hidden: true,
  }),
  S({
    id: "profes.capacitacion.final",
    title: "Examen final del curso",
    path: "/profes/capacitacion/final",
    area: "profes",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-08)",
    status: "built",
    roles: ["teacher"],
    hidden: true,
  }),
  S({
    id: "profes.capacitacion.certificado",
    title: "Certificado del curso",
    path: "/profes/capacitacion/certificado",
    area: "profes",
    section: "capacitacion",
    spec: "Capacitación (RN-CAP-08)",
    status: "built",
    roles: ["teacher"],
    hidden: true,
  }),
];

// ─────────────────────────────────────────────────────────────────────────────
// §2.4 Tablet kiosko · §2.5 Punto de acceso
// ─────────────────────────────────────────────────────────────────────────────

const KIOSK_SCREENS: ScreenDef[] = [
  S({
    id: "kiosko.espera",
    title: "Kiosko en espera",
    path: "/kiosko",
    area: "kiosko",
    section: "inicio",
    spec: "§2.4",
    status: "built",
    purpose: "Pantalla de reposo de la tablet: reloj, sede y a la espera de un pedido de firma.",
  }),
  S({
    id: "kiosko.firma",
    title: "Firma en mostrador",
    path: "/kiosko/firma",
    area: "kiosko",
    section: "socios",
    spec: "§3",
    status: "built",
    purpose:
      "Firma manuscrita de consentimientos, declaración física, autorizaciones de menores y retiros.",
  }),
  S({
    id: "kiosko.pagar",
    title: "Pagar cuota",
    path: "/kiosko/pagar",
    area: "kiosko",
    section: "planes",
    spec: "§4",
    status: "built",
    // La construyó el módulo §9 (kiosko), que es el dueño de `/api/kiosk/dues/*`.
    module: "Kiosko y stock (§9)",
    purpose: "Autopago con MP Point/QR que impacta cuenta corriente y caja y desbloquea el acceso.",
  }),
  S({
    id: "kiosko.alta-visitante",
    title: "Alta express de visitante por convenio",
    path: "/kiosko/visitante",
    area: "kiosko",
    section: "acceso",
    spec: "§6",
    status: "built",
    purpose:
      "Ficha digital de primera visita SportClub/Wellhub con declaración, reglamento y firma.",
  }),
  S({
    id: "kiosko.fichaje",
    title: "Fichaje de personal",
    path: "/kiosko/fichaje",
    area: "kiosko",
    section: "profesores",
    spec: "§6 / §7",
    status: "built",
    purpose: "Entrada/salida por QR personal o PIN; alimenta la liquidación de horas.",
  }),
  S({
    id: "kiosko.venta",
    title: "Punto de venta de kiosko",
    path: "/kiosko/venta",
    area: "kiosko",
    section: "contable",
    spec: "§9",
    status: "built",
    module: "Kiosko y stock (§9)",
    purpose: "Venta de productos operada por la recepcionista con cobro en el momento.",
  }),
  S({
    id: "kiosko.qr-salon",
    title: "QR de salón",
    path: "/kiosko/qr-salon",
    area: "kiosko",
    section: "clases",
    spec: "§5",
    status: "built",
    purpose: "Cartel con el QR único por salón que la app escanea para marcar asistencia.",
  }),
  S({
    id: "kiosko.semaforo",
    title: "Semáforo de ingreso",
    path: "/kiosko/semaforo",
    area: "kiosko",
    section: "acceso",
    spec: "§6",
    status: "built",
    // La tablet la abre con su identidad de dispositivo (el área kiosko no filtra por rol);
    // el rol mínimo es el de la tarjeta del Mostrador: recepción para arriba (RN-ACC-07).
    minRole: "receptionist",
    purpose:
      "Verde/amarillo/rojo con motivo, iniciales y distintivos; paso manual, excepción y contingencia.",
  }),
];

/** El registro completo. Es la fuente de `nav.ts` y de `routes.tsx`. */
export const SCREENS: ScreenDef[] = [...ADMIN_SCREENS, ...TEACHER_SCREENS, ...KIOSK_SCREENS];

export const SCREENS_BY_ID: Record<string, ScreenDef> = Object.fromEntries(
  SCREENS.map((screen) => [screen.id, screen]),
);

export function screensOfArea(area: Area): ScreenDef[] {
  return SCREENS.filter((screen) => screen.area === area);
}

export function builtScreens(): ScreenDef[] {
  return SCREENS.filter((screen) => screen.status === "built");
}

export function pendingScreens(): ScreenDef[] {
  return SCREENS.filter((screen) => screen.status === "pending");
}

/**
 * Rutas viejas del portal admin que siguen andando como redirección: links guardados y
 * mails ya enviados apuntan ahí. Ruta vieja → ruta actual.
 */
export const ADMIN_REDIRECTS: Readonly<Record<string, string>> = {
  // Planes vigentes (Administración) se unificó en Configuración → Planes y precios (AF
  // §4.10); los avisos de vencimiento de planes mandados antes linkean a la ruta vieja.
  "/admin/planes/vigentes": "/admin/planes",
};

/** Ruta de una pantalla por id — para no escribir strings sueltos en los links. */
export function pathOf(id: string): string {
  const screen = SCREENS_BY_ID[id];
  if (!screen) throw new Error(`Pantalla desconocida: ${id}`);
  return screen.path;
}
